import { cn } from '@/lib/utils';
import { CircleHelp, Lock, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { ExampleButton } from '@/components/welcome/WelcomeCard';
import {
  OPEN_TOPIC_GUIDE_EVENT,
  TOPIC_GUIDE_PULSE_EVENT,
  dayLabel,
  filterTopics,
  refreshWelcome
} from '@/components/welcome/api';
import { useWelcome } from '@/components/welcome/useWelcome';

/**
 * "What can I ask?" -- the full list of topics this user may ask about, at any
 * time. The welcome card is shown once; this is where it lives afterwards, so
 * closing the card never loses the information.
 *
 * REST only. It never opens a socket, so it never reserves a container.
 */
export default function TopicGuideNav() {
  const { enabled, data } = useWelcome();
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [pulse, setPulse] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOpen = (e: Event) => {
      setFocus((e as CustomEvent<string | null>).detail ?? null);
      setQuery('');
      setOpen(true);
      refreshWelcome();
    };
    const onPulse = () => {
      setPulse(true);
      window.setTimeout(() => setPulse(false), 4200);
    };
    window.addEventListener(OPEN_TOPIC_GUIDE_EVENT, onOpen);
    window.addEventListener(TOPIC_GUIDE_PULSE_EVENT, onPulse);
    return () => {
      window.removeEventListener(OPEN_TOPIC_GUIDE_EVENT, onOpen);
      window.removeEventListener(TOPIC_GUIDE_PULSE_EVENT, onPulse);
    };
  }, []);

  // Scroll the asked-for topic into view once the sheet has rendered it.
  useEffect(() => {
    if (!open || !focus) return;
    const t = window.setTimeout(() => {
      bodyRef.current
        ?.querySelector(`[data-topic="${CSS.escape(focus)}"]`)
        ?.scrollIntoView({ block: 'center' });
    }, 120);
    return () => window.clearTimeout(t);
  }, [open, focus]);

  const topics = useMemo(
    () => filterTopics(data?.topics ?? [], query),
    [data, query]
  );

  // No labelled topics (an older tenant, or one not onboarded to the guide):
  // a button that opens an empty list helps nobody, so there is no button.
  if (!enabled || !data?.topics.length) return null;
  const total = data.topics.length;
  const lagging = data?.topics.some((t) => t.stale);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            id="topic-guide-button"
            variant="ghost"
            onClick={() => {
              setFocus(null);
              setQuery('');
              setOpen(true);
              refreshWelcome();
            }}
            aria-label="What can I ask?"
            className={cn(
              'relative gap-1.5 px-2 text-muted-foreground hover:text-foreground',
              pulse &&
                'after:pointer-events-none after:absolute after:-inset-1 after:rounded-xl after:border-2 after:border-[var(--sig-blue)] after:animate-ping motion-reduce:after:animate-none'
            )}
          >
            <CircleHelp className="!size-5" />
            <span className="hidden lg:inline text-[13px]">
              What can I ask?
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>What can I ask?</TooltipContent>
      </Tooltip>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-[440px]"
        >
          <SheetHeader className="gap-2 space-y-0 border-b border-[var(--sig-border)] px-5 pb-3 pt-5 text-left">
            <SheetTitle className="text-base">What can I ask?</SheetTitle>
            <SheetDescription className="text-[13px]">
              {total
                ? `${total} ${total === 1 ? 'topic' : 'topics'} from your ERP, updated every night${lagging ? ' (some are behind, see below)' : ''}. Click a question to put it in the message box.`
                : 'No topics are listed for your account yet.'}
            </SheetDescription>
            {total ? (
              <label className="flex items-center gap-2 rounded-[9px] border border-[var(--sig-border-strong)] px-2.5 py-1.5 text-[var(--sig-subtle)] focus-within:border-[var(--sig-blue)]">
                <Search className="size-4" />
                <input
                  id="topic-guide-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search topics or questions"
                  autoComplete="off"
                  className="min-w-0 flex-1 bg-transparent text-sm text-[var(--sig-ink)] outline-none"
                />
              </label>
            ) : null}
          </SheetHeader>

          <div ref={bodyRef} className="flex-1 overflow-y-auto px-3 pb-4 pt-2">
            {topics.map((t) => {
              const through = dayLabel(t.updated_through);
              return (
                <div
                  key={t.id}
                  data-topic={t.id}
                  className={cn(
                    'grid gap-1.5 rounded-[10px] border-b border-[var(--sig-border)] px-2.5 py-3 last:border-b-0',
                    focus === t.id && 'bg-[var(--sig-tint)]'
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-[var(--sig-ink)]">
                      {t.label}
                    </h3>
                    {through ? (
                      <span
                        className={cn(
                          'ml-auto whitespace-nowrap text-[11.5px] tabular-nums',
                          t.stale
                            ? 'font-medium text-[var(--sig-warn-icon)]'
                            : 'text-[var(--sig-subtle)]'
                        )}
                      >
                        through {through}
                        {t.stale ? ' · not updating' : ''}
                      </span>
                    ) : null}
                  </div>
                  {t.summary ? (
                    <p className="text-[13px] text-[var(--sig-body)]">
                      {t.summary}
                    </p>
                  ) : null}
                  {t.examples.map((q) => (
                    <ExampleButton
                      key={q}
                      question={q}
                      onPick={() => setOpen(false)}
                    />
                  ))}
                </div>
              );
            })}
            {total > 0 && topics.length === 0 ? (
              <p className="px-3 py-7 text-center text-[13px] text-[var(--sig-muted)]">
                No topic matches “{query}”. Your data may not include it.
              </p>
            ) : null}
          </div>

          <div className="flex items-start gap-2 border-t border-[var(--sig-border)] px-5 py-3 text-[12.5px] text-[var(--sig-muted)]">
            <Lock className="mt-0.5 size-3.5 shrink-0" />
            <span>
              Need a topic that is not listed? Your company admin controls which
              data you can ask about.
            </span>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
